// src/lib/footprint-editor/padAxis.js
//
// The pad axis (S6b prerequisite 2): a view-and-snap aid that turns the grid
// and the snap frame to the pad, so a layout on a pad that is not square to
// grid north can be drawn square to the pad. Nothing here is stored: a
// feature's rotation_deg stays absolute (clockwise from grid north) and the
// axis is recomputed from the perimeter whenever it is asked for.
//
// ── THE AXIS ───────────────────────────────────────────────────────────────
//
// In the revision's frame (local_epsg, frame.js), in meters:
//
//   long side   the minimum-area rotated rectangle of the perimeter's outer
//               rings (rotating calipers over their convex hull: one of its
//               sides lies on a hull edge), and the bearing of its longer
//               side. A tie in area between orientations goes to the
//               smaller bearing.
//   edge        the bearing of one perimeter edge, picked by the person.
//
// A bearing is clockwise from grid north, as rotation_deg is, in [0, 180):
// an axis has no direction. The grid turns about the perimeter's centroid,
// which is a dot.
//
// A NEAR-SQUARE pad has two candidate long sides 90° apart, and which one is
// "longer" can flip with a vertex moved by a hair. That changes nothing a
// person draws with: the grid is the same grid turned by 90°, and 90° is a
// whole number of 15° rotation steps, so every snapped anchor and every
// snapped rotation is the same either way. Only the "relative to pad"
// reading changes, by 90°. `ratio` (long / short) says how near square it is
// and NEAR_SQUARE_RATIO is where the page says so.
//
// ── THE SNAP FRAME ─────────────────────────────────────────────────────────
//
// toAxis / fromAxis turn frame meters into pad meters (x across the axis, y
// along it, from the centroid) and back. The editor snaps a point to the
// grid in pad meters (geometry.js snapPointToGrid with an axis) and Paper
// draws its dots at whole steps in pad meters (axisFrame), through the same
// fromAxis, so a snapped anchor lands on a dot to the last bit. With no axis
// both are the absolute grid, exactly as before.

export const NEAR_SQUARE_RATIO = 1.05;
const rad = Math.PI / 180;

/** A bearing (degrees clockwise from grid north) as an axis: in [0, 180). */
export function axisDeg(deg) {
  const d = ((deg % 180) + 180) % 180;
  return d === 180 ? 0 : d;
}

/** The bearing of a to b in the frame, as an axis. */
export function bearingOf(a, b) {
  return axisDeg(Math.atan2(b[0] - a[0], b[1] - a[1]) / rad);
}

/** Frame meters [E, N] to pad meters [x across, y along], about axis.origin. */
export function toAxis([e, n], axis) {
  const c = Math.cos(axis.deg * rad), s = Math.sin(axis.deg * rad);
  const dx = e - axis.origin[0], dy = n - axis.origin[1];
  return [dx * c - dy * s, dx * s + dy * c];
}

/** Pad meters back to frame meters: toAxis's inverse. */
export function fromAxis([x, y], axis) {
  const c = Math.cos(axis.deg * rad), s = Math.sin(axis.deg * rad);
  return [axis.origin[0] + x * c + y * s, axis.origin[1] - x * s + y * c];
}

/**
 * A frame (frame.js's shape: toLocal, toLngLat, epsg) whose local axes are
 * the pad's: what Paper's grid is drawn in. With no axis, the frame itself.
 */
export function axisFrame(frame, axis) {
  if (!frame || !axis) return frame;
  return {
    epsg: frame.epsg, axis,
    toLocal: (lngLat) => toAxis(frame.toLocal(lngLat), axis),
    toLngLat: (xy) => frame.toLngLat(fromAxis(xy, axis)),
  };
}

/** The perimeter's rings in the frame: [{outer: [[E, N]...], holes: [...]}] per polygon. */
function polygonsLocal(perimeter, frame) {
  const polys = perimeter?.type === "Polygon" ? [perimeter.coordinates]
    : perimeter?.type === "MultiPolygon" ? perimeter.coordinates : [];
  return polys.filter((p) => p?.[0]?.length >= 4).map((p) => p.map((ring) => ring.map((q) => frame.toLocal(q))));
}

function ringArea(r) {
  let a = 0;
  for (let i = 1; i < r.length; i++) a += r[i - 1][0] * r[i][1] - r[i][0] * r[i - 1][1];
  return a / 2;
}

function ringCentroid(r) {
  let a = 0, cx = 0, cy = 0;
  for (let i = 1; i < r.length; i++) {
    const k = r[i - 1][0] * r[i][1] - r[i][0] * r[i - 1][1];
    a += k; cx += (r[i - 1][0] + r[i][0]) * k; cy += (r[i - 1][1] + r[i][1]) * k;
  }
  return a ? [cx / (3 * a), cy / (3 * a)] : null;
}

/** The area centroid of a perimeter's polygons in the frame (holes subtracted), or null. */
export function perimeterCentroid(perimeter, frame) {
  let A = 0, X = 0, Y = 0;
  // Relative to a point on the shape: UTM eastings and northings are large.
  const polys = polygonsLocal(perimeter, frame);
  if (!polys.length) return null;
  const o = polys[0][0][0];
  for (const poly of polys) {
    poly.forEach((ring, i) => {
      const r = ring.map((p) => [p[0] - o[0], p[1] - o[1]]);
      const a = Math.abs(ringArea(r)), c = ringCentroid(r);
      if (!c || !a) return;
      const w = i === 0 ? a : -a;
      A += w; X += c[0] * w; Y += c[1] * w;
    });
  }
  return A > 0 ? [o[0] + X / A, o[1] + Y / A] : null;
}

function convexHull(points) {
  const p = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (p.length < 3) return p;
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower = [], upper = [];
  for (const q of p) { while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], q) <= 0) lower.pop(); lower.push(q); }
  for (let i = p.length - 1; i >= 0; i--) { const q = p[i]; while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], q) <= 0) upper.pop(); upper.push(q); }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

/**
 * The minimum-area rotated rectangle of points in the frame: its long side's
 * bearing (an axis), the side lengths and their ratio. null under 3 points
 * or with no area.
 * @returns {{deg: number, longM: number, shortM: number, ratio: number}|null}
 */
export function minRectAxis(points) {
  const h = convexHull(points);
  if (h.length < 3) return null;
  let best = null;
  for (let i = 0; i < h.length; i++) {
    const a = h[i], b = h[(i + 1) % h.length];
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (!L) continue;
    const u = [(b[0] - a[0]) / L, (b[1] - a[1]) / L];
    let u0 = Infinity, u1 = -Infinity, v0 = Infinity, v1 = -Infinity;
    for (const q of h) {
      const pu = q[0] * u[0] + q[1] * u[1], pv = -q[0] * u[1] + q[1] * u[0];
      u0 = Math.min(u0, pu); u1 = Math.max(u1, pu); v0 = Math.min(v0, pv); v1 = Math.max(v1, pv);
    }
    const along = u1 - u0, across = v1 - v0, area = along * across;
    if (!(area > 0)) continue;
    const edgeDeg = bearingOf(a, b);
    const deg = along >= across ? edgeDeg : axisDeg(edgeDeg + 90);
    const c = { deg, longM: Math.max(along, across), shortM: Math.min(along, across), area };
    const tie = best && Math.abs(c.area - best.area) <= 1e-9 * best.area;
    if (!best || (!tie && c.area < best.area) || (tie && c.deg < best.deg)) best = c;
  }
  return best && { deg: best.deg, longM: best.longM, shortM: best.shortM, ratio: best.longM / best.shortM };
}

/** The perimeter's edges in the frame, as [{ring, index, a, b}], ring by ring across polygons. */
export function perimeterEdges(perimeter, frame) {
  const out = [];
  let ring = 0;
  for (const poly of polygonsLocal(perimeter, frame)) {
    for (const r of poly) {
      for (let i = 1; i < r.length; i++) out.push({ ring, index: i - 1, a: r[i - 1], b: r[i] });
      ring++;
    }
  }
  return out;
}

/**
 * The pad axis for a choice: {mode: "long"} or {mode: "edge", ring, index}.
 * null when off, with no perimeter, or naming an edge the perimeter does not
 * have (it changed since the edge was picked).
 * @returns {{origin: number[], deg: number, source: "long"|"edge", ratio: number|null, nearSquare: boolean}|null}
 */
export function padAxis(perimeter, frame, choice) {
  if (!frame || !choice || (choice.mode !== "long" && choice.mode !== "edge")) return null;
  const origin = perimeterCentroid(perimeter, frame);
  if (!origin) return null;
  if (choice.mode === "edge") {
    const e = perimeterEdges(perimeter, frame).find((x) => x.ring === choice.ring && x.index === choice.index);
    if (!e || (e.a[0] === e.b[0] && e.a[1] === e.b[1])) return null;
    return { origin, deg: bearingOf(e.a, e.b), source: "edge", ratio: null, nearSquare: false };
  }
  const outer = polygonsLocal(perimeter, frame).flatMap((poly) => poly[0]);
  const r = minRectAxis(outer);
  if (!r) return null;
  return { origin, deg: r.deg, source: "long", ratio: r.ratio, nearSquare: r.ratio < NEAR_SQUARE_RATIO };
}

/** The perimeter edge nearest en within tolM, or null: what a click on an edge picks. */
export function edgeNear(perimeter, frame, en, tolM) {
  let best = null, bestD = tolM;
  for (const e of perimeterEdges(perimeter, frame)) {
    const dx = e.b[0] - e.a[0], dy = e.b[1] - e.a[1], L2 = dx * dx + dy * dy;
    if (!L2) continue;
    const t = Math.max(0, Math.min(1, ((en[0] - e.a[0]) * dx + (en[1] - e.a[1]) * dy) / L2));
    const d = Math.hypot(e.a[0] + t * dx - en[0], e.a[1] + t * dy - en[1]);
    if (d <= bestD) { bestD = d; best = { ring: e.ring, index: e.index, deg: bearingOf(e.a, e.b) }; }
  }
  return best;
}

/** A rotation relative to the axis, for the properties panel: [0, 360). */
export function relativeDeg(rotationDeg, axis) {
  const d = (((rotationDeg ?? 0) - (axis?.deg ?? 0)) % 360 + 360) % 360;
  return d === 360 ? 0 : d;
}
